import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import Header from "../components/common/Header";
import Footer from "../components/common/Footer";

// ── FAQ data ──────────────────────────────────────────────────────────────────

const FAQ_SECTIONS = [
  {
    id: "general",
    title: "General",
    items: [
      {
        q: "What is GigFalcon?",
        a: "GigFalcon is a map-based freelance and job marketplace. It puts every freelancer, local service provider, and employer on one interactive map so customers and employers can discover the right person nearby — or anywhere in the world.",
      },
      {
        q: "How is GigFalcon different from other Marketplaces?",
        a: "GigFalcon combines local jobs, services, and freelance opportunities into one platform. Unlike traditional marketplaces, users can discover opportunities and talent based on location through our real-time marketplace map."
      },
      {
        q: "Who can use GigFalcon?",
        a: "GigFalcon is for anyone looking to hire, get hired, earn extra income, or grow their business, including: \n • Employers and businesses hiring workers \n • Homeowners and business owners looking for local services \n • Service providers and contractors seeking more customers \n • Freelancers offering skills and services \n • Job seekers looking for full-time, part-time, or temporary work \n • Employees who want to earn extra income after work or on weekends \n • Professionals and tradespeople looking to build their reputation and grow their customer base"
      },
      {
        q: "Is GigFalcon free to join?",
        a: "Yes. Creating an account and exploring opportunities on GigFalcon is free."
      },
      {
        q: "How does GigFalcon's map work?",
        a: "Our interactive map helps users discover jobs, gigs, services, and talent based on location, making it easier to find nearby opportunities and professionals."
      },
      {
        q: "Can I chat with employers, service providers or customers/clients?",
        a: "Yes. GigFalcon includes direct messaging so users can communicate, ask questions, discuss requirements, and coordinate projects."
      },
      {
        q: "How does GigFalcon make money?",
        a: "GigFalcon charges transaction-based service fees to service providers and clients and offers affordable subscriptions for job/hiring posting options to small businesses and employers. First Job is always free to post."
      },
      {
        q: "Is GigFalcon available as a mobile app?",
        a: "Yes. GigFalcon is available on both iOS (App Store) and Android (Google Play). Search for 'Gig Falcon' to download the free app and access the full marketplace on your phone.",
      },
      {
        q: "How does the AI search work?",
        a: 'Type what you need in plain English — for example "plumber near me under $200" or "React developer for a startup project". The AI automatically extracts the location, budget, service type, and content type, then navigates the map and filters results for you instantly.',
      },
      {
        q: "How do I contact GigFalcon support?",
        a: "Email us at support@gigfalcon.com. We also respond on our social channels linked in the footer. We typically reply within 24 hours on business days.",
      },
      {
        q: "Is GigFalcon available outside the United States?",
        a: "GigFalcon supports freelancers and clients worldwide. While our largest user base is currently in the United States, the map-first approach works for any location globally — simply pan the map to any city or country.",
      },
    ],
  },
  {
    id: "clients",
    title: "FOR CLIENTS (BUYERS / CUSTOMERS / TASK & PROJECT POSTERS)",
    items: [
      {
        q: "How do I find a service provider near me?",
        a: "Open GigFalcon and you'll see an interactive map centred on your location. Every pin represents a real freelancer or service provider nearby. Click any pin to view their profile, pricing, reviews, and availability.",
      },
      {
        q: "How do I hire local service providers or freelancers?",
        a: "Search the marketplace, browse profiles, review ratings, chat directly with providers, and hire the professional that best fits your needs."
      },
      {
        q: "Is it free to browse and hire?",
        a: "Yes. Browsing the map and messaging providers is completely free. You only pay when you agree on a project and place an order through the platform.",
      },
      {
        q: "How is my payment protected?",
        a: "All payments are held in escrow until you confirm the work is complete and satisfactory. If a dispute arises, our support team will review the case and assist with a resolution or refund if warranted.",
      },
      {
        q: "Can I post a project and receive proposals?",
        a: "Yes. Post a project with your requirements and budget under the Buyer section. Freelancers in your area — or globally — can submit proposals for you to review, compare, and accept.",
      },
      {
        q: "What if I'm not satisfied with the work?",
        a: "You can request revisions from the provider before accepting delivery. If the issue cannot be resolved through revisions, you can open a dispute and our support team will step in to help reach a fair outcome.",
      },
    ],
  },
  {
    id: "employers",
    title: "For Employers",
    items: [
      {
        q: "Why should I post jobs on GigFalcon?",
        a: "GigFalcon helps employers reach local candidates, freelancers, and service providers through a single platform designed for real-time hiring and workforce discovery."
      },
      {
        q: "How much does it cost to post a job?",
        a: "Your first job posting is FREE. After that, job postings start at just $9.99 per month. Need to hire for multiple roles? GigFalcon offers affordable subscription packages to help businesses attract and manage talent more efficiently."
      },
      {
        q: "Can I hire someone without having a company website?",
        a: "Absolutely. GigFalcon helps small businesses and local employers post jobs and hire talent even if they don't have a website."
      },
      {
        q: "Can I hire freelancers, contractors, and employees?",
        a: "Yes. GigFalcon supports full-time jobs, part-time jobs, contract work, freelance projects, and local service requests."
      },
      {
        q: "How do I post a job on GigFalcon?",
        a: 'Go to "Create a Job" in your Employer dashboard, fill in the job title, description, requirements, and salary range. Your job post will immediately appear as a pin on the map so nearby job seekers can find it.',
      },
      {
        q: "What is the difference between a Job and a Project?",
        a: "A Job is a traditional employment opportunity — full-time, part-time, or contract — posted by an Employer. A Project is a short-term deliverable posted by a Buyer/Client. Both appear on the map with distinct pin icons.",
      },
      {
        q: "What subscription plans are available for employers?",
        a: "GigFalcon offers several subscription tiers that increase the number of active job posts, boost featured placement, and expand your candidate reach. Visit the Employer Pricing page for a full plan comparison.",
      },
      {
        q: "How do I manage incoming job applications?",
        a: 'All candidates who apply to your job post appear in "Incoming Candidates" in your dashboard. You can review their profiles, message them directly through the platform, and shortlist or decline applications.',
      },
      {
        q: "Can I see where applicants are located on the map?",
        a: "Yes. Each applicant's profile includes their location, which is reflected on the map. This helps you prioritise local candidates when on-site presence is required for the role.",
      },
    ],
  },

  {
    id: "freelancers",
    title: "For Freelancers & Service Providers",
    items: [
      {
        q: "How do I list my services on GigFalcon?",
        a: "Create a free account, switch to Seller mode, and create a Gig. Your pin will appear on the map immediately so local clients can discover you. You can add a profile photo, description, portfolio images, and set your hourly or fixed rate.",
      },
      {
        q: "How do I get paid for my work?",
        a: "Payment is held in escrow when a client places an order. Once the work is delivered and accepted, the funds are released to your GigFalcon account. You can withdraw via bank transfer at any time.",
      },
      {
        q: "Is there a fee to join as a service provider?",
        a: "Creating an account and listing your services is completely free. GigFalcon charges a small service fee only when you complete a paid order — there are no upfront or monthly costs for sellers.",
      },
      {
        q: "Can I offer both local and remote services?",
        a: "Yes. When creating your gig, you can specify whether you offer in-person local services, remote work, or both. The map will reflect your availability and location accordingly.",
      },
      {
        q: "How do I receive more orders?",
        a: "Complete your profile with a clear photo, a detailed bio, and portfolio samples. Respond to client inquiries quickly, keep your availability up to date, and collect reviews after every completed job to build trust and rank higher on the map.",
      },
      {
        q: "Do I need to bid on projects?",
        a: "No. GigFalcon focuses on direct connections between customers and providers, helping reduce the need for complicated bidding processes."
      }
    ],
  },
  {
    id: "payments",
    title: "For Payments, Trust & Safety",
    items: [
      {
        q: "How are payments protected on GigFalcon?",
        a: "GigFalcon uses an escrow-style payment system to help protect both customers and service providers. Funds from customers/clients are securely held until the work is completed and approved by customers/clients, providing peace of mind for both parties.",
      },
      {
        q: "What is escrow and how does it work?",
        a: "Escrow is a secure payment process where funds are held by GigFalcon until the agreed-upon work is completed. Once the customer confirms satisfaction with the work, the payment is released to the service provider."
      },
      {
        q: "How does GigFalcon help prevent fraud?",
        a: "GigFalcon promotes a safer marketplace through secure payments, user profiles, reviews, ratings, direct messaging, and transaction tracking. We encourage users to communicate and complete payments through the platform for added protection."
      },
      {
        q: "When do service providers get paid?",
        a: "Service providers receive payment after the work has been completed and approved by the customer. This helps ensure fair transactions and builds trust between both parties."
      },
      {
        q: "What if there is a dispute between a customer and service provider?",
        a: "If a dispute arises, GigFalcon works with both parties to review the details and help facilitate a fair resolution before funds are released from escrow."
      },
      {
        q: "Are providers and employers verified?",
        a: "GigFalcon offers profile verification options and encourages reviews to help build trust within the community."
      },
      {
        q: "How do reviews work?",
        a: "After a completed job or service, users and service providers can leave reviews and ratings based on their experience."
      },
      {
        q: "Is my information secure?",
        a: "Yes. We use industry-standard security practices to protect user accounts and personal information."
      }
    ]
  }

];

// ── Accordion item ────────────────────────────────────────────────────────────

const AccordionItem = ({ item, isOpen, onToggle }) => (
  <div className="faq-item">
    <button className={`faq-question ${isOpen ? "open" : ""}`} onClick={onToggle}>
      <span className="faq-arrow">{isOpen ? "▲" : "▶"}</span>
      {item.q}
    </button>
    {isOpen && <div className="faq-answer">{item.a}</div>}
    <div className="faq-divider" />
  </div>
);

// ── Page component ────────────────────────────────────────────────────────────

const FAQ = () => {
  // Track which item is open: key = "sectionId-index"
  const [openKey, setOpenKey] = useState("0");

  const toggle = (key) => setOpenKey((prev) => (prev === key ? null : key));

  return (
    <>
      <Helmet>
        <title>FAQs — GigFalcon</title>
        <meta
          name="description"
          content="Find answers to the most common questions about GigFalcon — for freelancers, clients, employers, and general platform questions."
        />
      </Helmet>

      <Header />

      <PageStyled>
        {/* ── Hero ── */}
        <div className="faq-hero text-center">
          <div className="hero-accent-bar" />
          <h1 className="hero-title">We're Here to Help!</h1>
          <p className="hero-subtitle">Have questions? We've got answers.</p>
        </div>

        {/* ── Intro ── */}
        <div className="container faq-container">
          <p className="intro-text mb-3">
            Below are some of the most common questions from our community. If
            you don't find what you're looking for, please don't hesitate to
            reach out. You can email us at{" "}
            <a href="mailto:support@gigfalcon.com">support@gigfalcon.com</a>.
          </p>

          {/* ── Sections ── */}
          {FAQ_SECTIONS.map((section) => (
            <div key={section.id} className="faq-section">
              <h2 className="section-title">{section.title}</h2>
              {section.items.map((item, idx) => {
                const key = `${section.id}-${idx}`;
                return (
                  <AccordionItem
                    key={key}
                    item={item}
                    isOpen={openKey === key}
                    onToggle={() => toggle(key)}
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="faq-cta text-center">
          <p className="cta-text">Still have questions?</p>
          <a className="cta-btn" href="mailto:support@gigfalcon.com">
            Contact Support
          </a>
        </div>
      </PageStyled>

      <Footer />
    </>
  );
};

export default FAQ;

// ── Styles ────────────────────────────────────────────────────────────────────

const TEAL = "#0fd3bb";
const DARK = "#1c1c2e";
const GREY = "#666666";
const BORDER = "#e8e8e8";

const PageStyled = styled.div`
  background: #ffffff;
  min-height: 80vh;

  /* ── Hero ── */
  .faq-hero {
    padding: 48px 20px 32px;
    border-bottom: 1px solid ${BORDER};
  }

  .hero-accent-bar {
    width: 48px;
    height: 4px;
    background: ${TEAL};
    border-radius: 2px;
    margin: 0 auto 20px;
  }

  .hero-title {
    font-size: clamp(1.8rem, 4vw, 2.6rem);
    font-weight: 800;
    color: ${DARK};
    font-family: "MS_Bold", "Arial Black", sans-serif;
    margin-bottom: 8px;
  }

  .hero-subtitle {
    font-size: 1rem;
    color: ${GREY};
    margin-bottom: 0;
  }

  /* ── Container ── */
  .faq-container {
    max-width: 860px;
    padding-top: 32px;
    padding-bottom: 48px;
  }

  .intro-text {
    font-size: 15px;
    color: #444;
    line-height: 1.7;
    margin-bottom: 36px;

    a {
      color: ${TEAL};
      text-decoration: none;
      font-weight: 600;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  /* ── FAQ sections ── */
  .faq-section {
    margin-bottom: 40px;
  }

  .section-title {
    font-size: 1.3rem;
    font-weight: 800;
    color: ${DARK};
    font-family: "MS_Bold", "Arial Black", sans-serif;
    margin-bottom: 12px;
  }

  /* ── Accordion items ── */
  .faq-item {
    /* no extra wrapper needed */
  }

  .faq-question {
    width: 100%;
    background: none;
    border: none;
    outline: none;
    text-align: left;
    padding: 16px 4px;
    font-size: 15px;
    font-weight: 700;
    color: ${DARK};
    cursor: pointer;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-family: "MS_Bold", "Arial Black", sans-serif;
    transition: color 0.15s ease;

    &:hover {
      color: ${TEAL};
    }

    &.open {
      color: ${DARK};
    }
  }

  .faq-arrow {
    font-size: 10px;
    color: ${GREY};
    flex-shrink: 0;
    margin-top: 4px;
    transition: color 0.15s ease;
  }

  .faq-question.open .faq-arrow {
    color: ${TEAL};
  }

  .faq-answer {
    padding: 4px 4px 16px 24px;
    font-size: 15px;
    color: #555;
    line-height: 1.75;
  }

  .faq-divider {
    border-top: 1px solid ${BORDER};
  }

  /* ── Bottom CTA ── */
  .faq-cta {
    background: #f7f7f7;
    padding: 40px 20px;
    border-top: 1px solid ${BORDER};
  }

  .cta-text {
    font-size: 1.1rem;
    color: ${GREY};
    margin-bottom: 16px;
  }

  .cta-btn {
    display: inline-block;
    background: ${TEAL};
    color: #ffffff;
    padding: 12px 32px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 700;
    text-decoration: none;
    transition: background 0.2s ease;

    &:hover {
      background: #0bbea9;
      color: #ffffff;
    }
  }
`;
